import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '../home/components/Header';
import { Footer } from '../home/components/Footer';

export default function PrivacyPage() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('section1');

  // Safely extract the dynamically translated sections 
  const sections = t('privacy.sections', { returnObjects: true }) as Array<{
    id: string;
    title: string;
    blocks: any[];
  }>;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sectionNodes = document.querySelectorAll('[data-section]');
      let foundSection = '';

      for (const section of sectionNodes) {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 150 && rect.bottom >= 150) {
          foundSection = section.getAttribute('data-section') || '';
          break;
        }
      }

      if (foundSection) {
        setActiveSection(foundSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.querySelector(`[data-section="${sectionId}"]`);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-600 via-yellow-500 to-orange-500"></div>
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-black mb-4">
            {t('privacy.hero.title')}
          </h1>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Table of Contents - Sticky Sidebar */}
            <aside className="lg:w-80 flex-shrink-0">
              <div className="lg:sticky lg:top-24 bg-gray-800 rounded-2xl p-6 border border-gray-700">
                <h2 className="text-xl font-bold text-yellow-400 mb-4 flex items-center">
                  <i className="ri-list-check text-2xl mr-2"></i>
                  {t('privacy.toc.title')}
                </h2>
                <nav className="space-y-2 max-h-[60vh] overflow-y-auto custom-scrollbar">
                  {sections && Array.isArray(sections) && sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis ${
                        activeSection === section.id
                          ? 'bg-yellow-400 text-black font-semibold'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-yellow-400'
                      }`}
                    >
                      <span className="text-sm">{section.title}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 space-y-8">
              {sections && Array.isArray(sections) && sections.map((section) => (
                <div key={section.id} data-section={section.id} className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
                  <h2 className="text-2xl font-bold text-yellow-400 mb-6 flex items-center">
                    {section.title}
                  </h2>
                  
                  <div className="space-y-4 text-gray-300 text-base leading-relaxed">
                    {section.blocks.map((block, index) => {
                      if (Array.isArray(block)) {
                        return (
                          <ul key={index} className="space-y-2 ml-6">
                            {block.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <i className="ri-checkbox-blank-circle-fill text-[8px] text-yellow-400 mt-2 flex-shrink-0"></i>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      }
                      
                      // For regular string paragraphs
                      return (
                        <p key={index} className="whitespace-pre-wrap">
                           {block.includes('**') ? (
                            <span dangerouslySetInnerHTML={{ __html: block.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }} />
                          ) : (
                            block
                          )}
                        </p>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Final Statement */}
              <div className="bg-gradient-to-br from-yellow-400/10 to-orange-500/10 rounded-2xl p-8 border-2 border-yellow-400 mt-8">
                <p className="text-gray-300 text-center text-lg whitespace-pre-wrap">
                  {t('privacy.finalStatement')}
                </p>
              </div>

            </main>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}